/**
 * 언어 코드 변환 유틸리티
 * 앱 내부 언어 코드를 API 요청용 언어 코드로 변환
 */

// 앱 내부에서 사용하는 언어 코드 타입
export type AppLanguageCode = 'ko' | 'en' | 'zh';

// API 요청에 사용하는 언어 코드 타입
export type ApiLanguageCode = 'ko' | 'en' | 'zh-CN';

// 변환 매핑 (타입 안전성 보장)
const LANGUAGE_CODE_MAP: Record<AppLanguageCode, ApiLanguageCode> = {
  ko: 'ko',
  en: 'en',
  zh: 'zh-CN',
} as const;

// 표준 코드로 정규화가 필요한 비표준 언어 코드 별칭 (레거시/모바일 유래)
const LANGUAGE_CODE_ALIAS_MAP: Record<string, AppLanguageCode> = {
  kr: 'ko',
} as const;

/**
 * 임의의 언어 코드 문자열을 앱 표준 언어 코드(ko/en/zh)로 정규화
 * - 대소문자/공백/구분자('_' → '-') 정리 후 판별
 * - 'kr' 같은 비표준 별칭과 BCP-47 형태('ko-KR', 'zh-CN', 'en-US' 등)를 표준 코드로 매핑
 * @param input - 정규화할 언어 코드 (예: 'kr', 'ko-KR', 'EN', 'zh_CN')
 * @returns 표준 언어 코드(ko/en/zh), 판별 불가 시 null
 * @example
 * normalizeAppLanguage('ko-KR') // returns 'ko'
 * normalizeAppLanguage('kr') // returns 'ko'
 * normalizeAppLanguage('ja') // returns null
 */
export const normalizeAppLanguage = (
  input: string | null | undefined,
): AppLanguageCode | null => {
  if (!input) {
    return null;
  }

  // BCP-47 형태('ko-KR', 'zh_CN' 등)는 primary subtag 만 취해 판별
  const primaryTag = input.trim().toLowerCase().replace(/_/g, '-').split('-')[0];
  const normalized = LANGUAGE_CODE_ALIAS_MAP[primaryTag] ?? primaryTag;

  return normalized in LANGUAGE_CODE_MAP
    ? (normalized as AppLanguageCode)
    : null;
};

/**
 * 앱 언어 코드를 API 언어 코드로 변환
 * @param languageCode - 앱에서 사용하는 언어 코드 (ko, en, zh)
 * @returns API 요청에 사용할 언어 코드 (ko, en, zh-CN)
 * @example
 * getApiLanguageCode('zh') // returns 'zh-CN'
 * getApiLanguageCode('ko') // returns 'ko'
 */
export const getApiLanguageCode = (
  languageCode: AppLanguageCode,
): ApiLanguageCode => {
  const apiCode = LANGUAGE_CODE_MAP[languageCode];

  if (!apiCode) {
    console.error(`등록되지 않은 언어 코드 입니다: ${languageCode}`);
    return 'ko'; // fallback to Korean
  }

  return apiCode;
};
